// CongressTrades.js - Congress Stock Trading Tracker
import React, { useState, useEffect } from 'react';
import {
  Users,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Loader,
  Search,
  Filter,
  AlertTriangle,
  Building,
  DollarSign,
  Calendar,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
  User,
  BarChart3,
  Info
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

const CongressTrades = () => {
  const [activeTab, setActiveTab] = useState('recent');
  const [trades, setTrades] = useState([]);
  const [politicians, setPoliticians] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [tradesPage, setTradesPage] = useState(1);
  const [politiciansPage, setPoliticiansPage] = useState(1);
  const [trendingPage, setTrendingPage] = useState(1);
  const [hasMoreTrades, setHasMoreTrades] = useState(false);
  const [hasMorePoliticians, setHasMorePoliticians] = useState(false);
  const [hasMoreTrending, setHasMoreTrending] = useState(false);
  const [totalTrades, setTotalTrades] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Filters
  const [searchTicker, setSearchTicker] = useState('');
  const [searchPolitician, setSearchPolitician] = useState('');
  const [filterParty, setFilterParty] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterChamber, setFilterChamber] = useState('all');
  const [filterDays, setFilterDays] = useState(180);

  useEffect(() => {
    // Reset pagination when days filter changes
    setTradesPage(1);
    setPoliticiansPage(1);
    setTrendingPage(1);
    setTrades([]);
    setPoliticians([]);
    setTrending([]);
    fetchData(1, true);
  }, [filterDays]);

  const fetchData = async (page = 1, reset = false) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const [tradesRes, politiciansRes, trendingRes] = await Promise.all([
        fetch(`${API_BASE}/congress/trades?page=${page}&limit=50&days=${filterDays}`),
        fetch(`${API_BASE}/congress/politicians?page=${page}&limit=20&days=${filterDays}`),
        fetch(`${API_BASE}/congress/trending?page=${page}&limit=20&days=${filterDays}`)
      ]);

      if (tradesRes.ok) {
        const data = await tradesRes.json();
        if (reset) {
          setTrades(data.trades || []);
        } else {
          setTrades(prev => [...prev, ...(data.trades || [])]);
        }
        setHasMoreTrades(data.hasMore || false);
        setTotalTrades(data.totalCount || 0);
        setTradesPage(data.page || 1);
      }
      if (politiciansRes.ok) {
        const data = await politiciansRes.json();
        if (reset) {
          setPoliticians(data.politicians || []);
        } else {
          setPoliticians(prev => [...prev, ...(data.politicians || [])]);
        }
        setHasMorePoliticians(data.hasMore || false);
        setPoliticiansPage(data.page || 1);
      }
      if (trendingRes.ok) {
        const data = await trendingRes.json();
        if (reset) {
          setTrending(data.trending || []);
        } else {
          setTrending(prev => [...prev, ...(data.trending || [])]);
        }
        setHasMoreTrending(data.hasMore || false);
        setTrendingPage(data.page || 1);
      }

    } catch (err) {
      console.error('Error fetching congress data:', err);
      setError('Failed to load congress trading data');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreTrades = async () => {
    setLoadingMore(true);
    try {
      const nextPage = tradesPage + 1;
      const res = await fetch(`${API_BASE}/congress/trades?page=${nextPage}&limit=50&days=${filterDays}`);
      if (res.ok) {
        const data = await res.json();
        setTrades(prev => [...prev, ...(data.trades || [])]);
        setHasMoreTrades(data.hasMore || false);
        setTradesPage(nextPage);
      }
    } catch (err) {
      console.error('Error loading more trades:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMorePoliticians = async () => {
    setLoadingMore(true);
    try {
      const nextPage = politiciansPage + 1;
      const res = await fetch(`${API_BASE}/congress/politicians?page=${nextPage}&limit=20&days=${filterDays}`);
      if (res.ok) {
        const data = await res.json();
        setPoliticians(prev => [...prev, ...(data.politicians || [])]);
        setHasMorePoliticians(data.hasMore || false);
        setPoliticiansPage(nextPage);
      }
    } catch (err) {
      console.error('Error loading more politicians:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreTrending = async () => {
    setLoadingMore(true);
    try {
      const nextPage = trendingPage + 1;
      const res = await fetch(`${API_BASE}/congress/trending?page=${nextPage}&limit=20&days=${filterDays}`);
      if (res.ok) {
        const data = await res.json();
        setTrending(prev => [...prev, ...(data.trending || [])]);
        setHasMoreTrending(data.hasMore || false);
        setTrendingPage(nextPage);
      }
    } catch (err) {
      console.error('Error loading more trending:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Filter trades
  const filteredTrades = trades.filter(trade => {
    if (searchTicker && trade.ticker && !trade.ticker.toUpperCase().includes(searchTicker.toUpperCase())) {
      return false;
    }
    if (searchPolitician && trade.politician && !trade.politician.toLowerCase().includes(searchPolitician.toLowerCase())) {
      return false;
    }
    if (filterParty !== 'all' && trade.party !== filterParty) {
      return false;
    }
    if (filterType === 'buy' && (!trade.type || !trade.type.toLowerCase().includes('purchase'))) {
      return false;
    }
    if (filterType === 'sell' && (!trade.type || !trade.type.toLowerCase().includes('sale'))) {
      return false;
    }
    if (filterChamber !== 'all' && trade.chamber !== filterChamber) {
      return false;
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getPartyColor = (party) => {
    if (!party) return '#94a3b8';
    if (party.toLowerCase().includes('democrat')) return '#3b82f6';
    if (party.toLowerCase().includes('republican')) return '#ef4444';
    return '#94a3b8';
  };

  const getPartyLabel = (party) => {
    if (!party) return '?';
    if (party.toLowerCase().includes('democrat')) return 'D';
    if (party.toLowerCase().includes('republican')) return 'R';
    return 'I';
  };

  const getTradeTypeColor = (type) => {
    if (!type) return '#94a3b8';
    if (type.toLowerCase().includes('purchase')) return '#22c55e';
    return '#ef4444';
  };

  const getTradeTypeLabel = (type) => {
    if (!type) return 'Unknown';
    if (type.toLowerCase().includes('purchase')) return 'BUY';
    if (type.toLowerCase().includes('sale_full')) return 'SELL (Full)';
    if (type.toLowerCase().includes('sale_partial')) return 'SELL (Partial)';
    if (type.toLowerCase().includes('sale')) return 'SELL';
    return type;
  };

  const LoadMoreButton = ({ onClick, hasMore, loading }) => {
    if (!hasMore) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
        <button
          onClick={onClick}
          disabled={loading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 2rem',
            background: 'rgba(59, 130, 246, 0.2)',
            border: '1px solid rgba(59, 130, 246, 0.4)',
            borderRadius: '0.5rem',
            color: '#60a5fa',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}
        >
          {loading ? <Loader size={18} className="spin" /> : <ChevronDown size={18} />}
          {loading ? 'Loading...' : 'Load More'}
        </button>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-30px',
          right: '-30px',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Building size={28} />
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Congress Trades Tracker</h1>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '0.9rem' }}>
                Track what U.S. Congress members are buying and selling • {totalTrades > 0 ? `${totalTrades} trades` : ''}
              </p>
            </div>
            <button
              onClick={() => fetchData(1, true)}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.625rem 1rem',
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '0.5rem',
                color: 'white',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              {loading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <Info size={20} color="#60a5fa" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ color: '#93c5fd', fontSize: '0.85rem', margin: '0 0 0.5rem 0', fontWeight: '600' }}>
            About STOCK Act Disclosures
          </p>
          <p style={{ color: '#bfdbfe', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
            Under the STOCK Act (2012), Congress members must disclose stock trades within 45 days. 
            This data is public and can provide insight into what informed legislators are investing in.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
        {[
          { id: 'recent', label: 'Recent Trades', icon: Calendar },
          { id: 'politicians', label: 'Top Traders', icon: Users },
          { id: 'trending', label: 'Trending Stocks', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: activeTab === tab.id ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 41, 59, 0.5)',
              border: activeTab === tab.id ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid #334155',
              borderRadius: '0.5rem',
              color: activeTab === tab.id ? '#60a5fa' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Time Period Filter */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Time Period:</span>
        {[30, 90, 180, 365].map(days => (
          <button
            key={days}
            onClick={() => setFilterDays(days)}
            style={{
              padding: '0.5rem 1rem',
              background: filterDays === days ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 41, 59, 0.5)',
              border: filterDays === days ? '1px solid rgba(59, 130, 246, 0.4)' : '1px solid #334155',
              borderRadius: '0.5rem',
              color: filterDays === days ? '#60a5fa' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.8rem'
            }}
          >
            {days === 365 ? '1 Year' : `${days} Days`}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Loader size={48} color="#3b82f6" className="spin" />
        </div>
      ) : error ? (
        <div style={{ padding: '2rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.75rem', textAlign: 'center' }}>
          <p style={{ color: '#fca5a5' }}>{error}</p>
          <button onClick={() => fetchData(1, true)}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#3b82f6', border: 'none', borderRadius: '0.5rem', color: 'white', cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      ) : (
        <>
          {/* Recent Trades Tab */}
          {activeTab === 'recent' && (
            <div>
              {/* Filters */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '0.75rem',
                marginBottom: '1rem',
                padding: '1rem',
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '0.75rem',
                border: '1px solid #334155'
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Ticker</label>
                  <input
                    type="text"
                    value={searchTicker}
                    onChange={(e) => setSearchTicker(e.target.value.toUpperCase())}
                    placeholder="e.g., AAPL"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid #334155',
                      borderRadius: '0.25rem',
                      color: 'white',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Politician</label>
                  <input
                    type="text"
                    value={searchPolitician}
                    onChange={(e) => setSearchPolitician(e.target.value)}
                    placeholder="e.g., Pelosi"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid #334155',
                      borderRadius: '0.25rem',
                      color: 'white',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Party</label>
                  <select
                    value={filterParty}
                    onChange={(e) => setFilterParty(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid #334155',
                      borderRadius: '0.25rem',
                      color: 'white',
                      fontSize: '0.85rem'
                    }}
                  >
                    <option value="all">All Parties</option>
                    <option value="Democrat">Democrat</option>
                    <option value="Republican">Republican</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Type</label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid #334155',
                      borderRadius: '0.25rem',
                      color: 'white',
                      fontSize: '0.85rem'
                    }}
                  >
                    <option value="all">Buy & Sell</option>
                    <option value="buy">Buys Only</option>
                    <option value="sell">Sells Only</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Chamber</label>
                  <select
                    value={filterChamber}
                    onChange={(e) => setFilterChamber(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid #334155',
                      borderRadius: '0.25rem',
                      color: 'white',
                      fontSize: '0.85rem'
                    }}
                  >
                    <option value="all">Both</option>
                    <option value="House">House</option>
                    <option value="Senate">Senate</option>
                  </select>
                </div>
              </div>

              {/* Results count */}
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
                Showing {filteredTrades.length} of {totalTrades} trades
              </p>

              {/* Trades Table */}
              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '0.75rem',
                border: '1px solid #334155',
                overflow: 'hidden'
              }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Date</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Politician</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Ticker</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Asset</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>Type</th>
                        <th style={{ padding: '0.75rem', textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTrades.map((trade, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '0.75rem', fontSize: '0.85rem' }}>
                            {formatDate(trade.transactionDate)}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: getPartyColor(trade.party),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                color: 'white'
                              }}>
                                {getPartyLabel(trade.party)}
                              </span>
                              <div>
                                <div style={{ fontWeight: '500', fontSize: '0.85rem' }}>{trade.politician}</div>
                                <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                                  {trade.chamber} {trade.state ? `• ${trade.state}` : ''}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '0.9rem',
                              color: trade.ticker && trade.ticker !== '--' ? '#60a5fa' : '#64748b'
                            }}>
                              {trade.ticker || 'N/A'}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#94a3b8', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {trade.asset || 'N/A'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              background: `${getTradeTypeColor(trade.type)}20`,
                              color: getTradeTypeColor(trade.type),
                              fontSize: '0.7rem',
                              fontWeight: '600'
                            }}>
                              {getTradeTypeLabel(trade.type)}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.85rem', color: '#e2e8f0' }}>
                            {trade.amount || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <LoadMoreButton onClick={loadMoreTrades} hasMore={hasMoreTrades} loading={loadingMore} />
            </div>
          )}

          {/* Politicians Tab */}
          {activeTab === 'politicians' && (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '1rem'
              }}>
                {politicians.map((pol, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: 'rgba(30, 41, 59, 0.5)',
                      borderRadius: '0.75rem',
                      padding: '1.25rem',
                      border: '1px solid #334155'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                      <span style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: getPartyColor(pol.party),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: 'white'
                      }}>
                        {getPartyLabel(pol.party)}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '1rem' }}>{pol.name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {pol.chamber} {pol.state ? `• ${pol.state}` : ''}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>{pol.totalTrades}</div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>trades</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{ flex: 1, padding: '0.5rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.5rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '600', color: '#22c55e' }}>{pol.buys}</div>
                        <div style={{ fontSize: '0.7rem', color: '#86efac' }}>Buys</div>
                      </div>
                      <div style={{ flex: 1, padding: '0.5rem', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.5rem', textAlign: 'center' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '600', color: '#ef4444' }}>{pol.sells}</div>
                        <div style={{ fontSize: '0.7rem', color: '#fca5a5' }}>Sells</div>
                      </div>
                    </div>

                    {pol.topStocks && pol.topStocks.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.5rem' }}>Top Stocks:</div>
                        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                          {pol.topStocks.map((stock, sIdx) => (
                            <span key={sIdx} style={{
                              padding: '0.25rem 0.5rem',
                              background: 'rgba(59, 130, 246, 0.2)',
                              borderRadius: '0.25rem',
                              fontSize: '0.7rem',
                              color: '#93c5fd'
                            }}>
                              {stock.symbol} ({stock.count})
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <LoadMoreButton onClick={loadMorePoliticians} hasMore={hasMorePoliticians} loading={loadingMore} />
            </div>
          )}

          {/* Trending Stocks Tab */}
          {activeTab === 'trending' && (
            <div>
              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '0.75rem',
                border: '1px solid #334155',
                overflow: 'hidden'
              }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>#</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Ticker</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Asset</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>Total Trades</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>Buys</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>Sells</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>Unique Traders</th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', color: '#94a3b8', fontSize: '0.75rem' }}>Recent Traders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trending.map((stock, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td style={{ padding: '0.75rem', color: '#64748b', fontSize: '0.85rem' }}>{idx + 1}</td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{ fontWeight: '600', fontSize: '1rem', color: '#60a5fa' }}>
                              {stock.ticker}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#94a3b8', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {stock.asset || '-'}
                          </td>
                          <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600' }}>{stock.totalTrades}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center', color: '#22c55e' }}>{stock.buys}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center', color: '#ef4444' }}>{stock.sells}</td>
                          <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              background: 'rgba(139, 92, 246, 0.2)',
                              borderRadius: '1rem',
                              fontSize: '0.8rem',
                              color: '#c4b5fd'
                            }}>
                              {stock.uniqueTraders}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                            {stock.recentTraders?.join(', ').substring(0, 50) || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <LoadMoreButton onClick={loadMoreTrending} hasMore={hasMoreTrending} loading={loadingMore} />
            </div>
          )}
        </>
      )}

      {/* Disclaimer */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '1rem',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.2)',
        borderRadius: '0.75rem',
        marginTop: '1.5rem'
      }}>
        <AlertTriangle size={20} color="#f87171" style={{ flexShrink: 0, marginTop: '2px' }} />
        <p style={{ color: '#fca5a5', fontSize: '0.8rem', margin: 0, lineHeight: '1.5' }}>
          <strong>Disclaimer:</strong> This data is sourced from public STOCK Act disclosures. 
          Congress members' trades are not financial advice. There may be legitimate reasons for trades 
          unrelated to stock performance. Always do your own research.
        </p>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default CongressTrades;